// Fase 13 (G5, ADR 0026): os passes e efeitos "cinematográficos" sobre o `postprocessing`. Só nomes
// importados um a um e só neste arquivo e em `post-effects.ts` (a cerca, `post-fence.test.ts`).
import { BlendFunction, Effect, EffectAttribute, Pass } from "postprocessing";
import { HalfFloatType, Matrix4, NoBlending, ShaderMaterial, Uniform, UnsignedByteType, Vector2, Vector3, Vector4, WebGLRenderTarget, type Camera, type DepthPackingStrategies, type Texture, type WebGLRenderer } from "three";

/** Vértice de tela cheia (o triângulo do `Pass`): uv de 0 a 1. */
const FULLSCREEN_VERTEX = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 1.0, 1.0);
}
`;

/** Trechos comuns: posição e normal de vista a partir da profundidade. */
const VIEW_FROM_DEPTH = /* glsl */ `
uniform sampler2D depthBuffer;
uniform mat4 projectionInverse;
float depthAt(const in vec2 uv) {
  return texture2D(depthBuffer, uv).r;
}
vec3 viewAt(const in vec2 uv, const in float d) {
  vec4 p = projectionInverse * vec4(vec3(uv, d) * 2.0 - 1.0, 1.0);
  return p.xyz / p.w;
}
`;

/**
 * Normais de vista a partir da profundidade (empacotadas em 0..1): o SSAO do pacote pede um buffer de
 * normais, e o mundo é facetado sem atributo de normal (o `NormalPass` desenharia a cena de novo com
 * normais zeradas). Em cada eixo, a diferença para o vizinho mais perto em profundidade (a silhueta não
 * entorta a normal). Céu: (0, 0, 1).
 */
const NORMALS_FRAGMENT = /* glsl */ `
${VIEW_FROM_DEPTH}
uniform vec2 texel;
varying vec2 vUv;
void main() {
  float d = depthAt(vUv);
  if (d >= 0.99999) {
    gl_FragColor = vec4(0.5, 0.5, 1.0, 1.0);
    return;
  }
  vec3 c = viewAt(vUv, d);
  vec3 l = viewAt(vUv - vec2(texel.x, 0.0), depthAt(vUv - vec2(texel.x, 0.0)));
  vec3 r = viewAt(vUv + vec2(texel.x, 0.0), depthAt(vUv + vec2(texel.x, 0.0)));
  vec3 b = viewAt(vUv - vec2(0.0, texel.y), depthAt(vUv - vec2(0.0, texel.y)));
  vec3 t = viewAt(vUv + vec2(0.0, texel.y), depthAt(vUv + vec2(0.0, texel.y)));
  // Na borda da tela o vizinho de fora repete o centro (diferença zero): vale o do outro lado.
  vec3 dr = r - c;
  vec3 dl = c - l;
  vec3 dt = t - c;
  vec3 db = c - b;
  vec3 dx = (abs(dr.z) < abs(dl.z) && dot(dr, dr) > 0.0) || dot(dl, dl) == 0.0 ? dr : dl;
  vec3 dy = (abs(dt.z) < abs(db.z) && dot(dt, dt) > 0.0) || dot(db, db) == 0.0 ? dt : db;
  vec3 n = cross(dx, dy);
  n = dot(n, n) > 0.0 ? normalize(n) : vec3(0.0, 0.0, 1.0);
  gl_FragColor = vec4(n * 0.5 + 0.5, 1.0);
}
`;

/** Base dos nossos passes de tela cheia que leem a profundidade do compositor e escrevem num alvo próprio. */
abstract class DepthReadingPass extends Pass {
  readonly renderTarget: WebGLRenderTarget;
  protected readonly material: ShaderMaterial;
  private readonly scale: number;
  /** A câmera da cena (a do compositor; `mainCamera` a troca). */
  protected view: Camera;

  constructor(name: string, camera: Camera, fragmentShader: string, uniforms: Record<string, Uniform>, scale: number, float: boolean) {
    super(name);
    this.view = camera;
    this.scale = scale;
    this.needsSwap = false;
    this.needsDepthTexture = true;
    this.material = new ShaderMaterial({
      name,
      uniforms: { depthBuffer: new Uniform(null), projectionInverse: new Uniform(new Matrix4()), ...uniforms },
      vertexShader: FULLSCREEN_VERTEX,
      fragmentShader,
      blending: NoBlending,
      depthTest: false,
      depthWrite: false,
    });
    this.fullscreenMaterial = this.material;
    this.renderTarget = new WebGLRenderTarget(1, 1, { type: float ? HalfFloatType : UnsignedByteType, depthBuffer: false });
    this.renderTarget.texture.name = name;
  }

  override set mainCamera(camera: Camera) {
    this.view = camera;
  }

  override setDepthTexture(depthTexture: Texture, _packing?: DepthPackingStrategies): void {
    this.material.uniforms["depthBuffer"]!.value = depthTexture;
  }

  override setSize(width: number, height: number): void {
    this.renderTarget.setSize(Math.max(1, Math.round(width * this.scale)), Math.max(1, Math.round(height * this.scale)));
    this.resized(width, height);
  }

  protected resized(_width: number, _height: number): void {}

  /** Antes de desenhar: as matrizes da câmera do quadro e o que mais o passe pedir. */
  protected prepare(_input: Texture): void {}

  override render(renderer: WebGLRenderer, inputBuffer: WebGLRenderTarget): void {
    (this.material.uniforms["projectionInverse"]!.value as Matrix4).copy(this.view.projectionMatrixInverse);
    this.prepare(inputBuffer.texture);
    renderer.setRenderTarget(this.renderTarget);
    renderer.render(this.scene, this.camera);
  }

  override dispose(): void {
    this.renderTarget.dispose();
    this.material.dispose();
    super.dispose();
  }
}

/** As normais de vista (alvo RGBA8 em resolução cheia), para o SSAO, o reflexo e a luz indireta. */
export class NormalsFromDepthPass extends DepthReadingPass {
  constructor(camera: Camera) {
    super("NormaisDaProfundidade", camera, NORMALS_FRAGMENT, { texel: new Uniform(new Vector2()) }, 1, false);
  }

  get texture(): Texture {
    return this.renderTarget.texture;
  }

  protected override resized(width: number, height: number): void {
    (this.material.uniforms["texel"]!.value as Vector2).set(1 / Math.max(1, width), 1 / Math.max(1, height));
  }
}

/**
 * Reflexo em espaço de tela (SSR) e luz indireta de um salto (GI) em meia resolução, somados num alvo
 * em meio-float (`IndirectEffect` junta à imagem). O reflexo vale no que olha para cima (o chão: o anel,
 * as estrelas e as arestas acesas refletem); a luz indireta espalha o que está aceso em volta (o neon
 * tinge o chão e o herói perto dele). As duas pesam o HDR: o cinza do mundo quase não reflete nem
 * ilumina, o neon sim. Ruído por pixel (gradiente intercalado); o `IndirectEffect` borra na volta.
 *
 * `settings`: x = força do reflexo (0 apaga), y = força da luz indireta (0 apaga), z = raio da luz
 * indireta (m), w = livre.
 */
const INDIRECT_FRAGMENT = /* glsl */ `
${VIEW_FROM_DEPTH}
uniform sampler2D inputBuffer;
uniform sampler2D normalBuffer;
uniform mat4 projection;
uniform mat4 cameraWorld;
uniform vec2 resolution;
uniform vec4 settings;
varying vec2 vUv;
const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);
vec3 normalAt(const in vec2 uv) {
  return normalize(texture2D(normalBuffer, uv).xyz * 2.0 - 1.0);
}
vec2 project(const in vec3 p) {
  vec4 c = projection * vec4(p, 1.0);
  return c.xy / c.w * 0.5 + 0.5;
}
bool outside(const in vec2 uv) {
  return uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0;
}
float ign(const in vec2 px) {
  return fract(52.9829189 * fract(dot(px, vec2(0.06711056, 0.00583715))));
}
void main() {
  float d = depthAt(vUv);
  if (d >= 0.99999) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }
  vec3 P = viewAt(vUv, d);
  vec3 N = normalAt(vUv);
  float noise = ign(gl_FragCoord.xy);
  vec3 result = vec3(0.0);
  if (settings.x > 0.0) {
    vec3 up = mat3(cameraWorld) * N;
    float mask = smoothstep(0.7, 0.93, up.y) * (1.0 - smoothstep(25.0, 60.0, -P.z));
    if (mask > 0.0) {
      vec3 V = normalize(P);
      vec3 R = normalize(reflect(V, N));
      float stepLen = 0.18 + noise * 0.12;
      vec3 pos = P + N * 0.02;
      vec3 prev = pos;
      vec2 hit = vec2(-1.0);
      float travel = 0.0;
      for (int i = 0; i < 28; i++) {
        prev = pos;
        pos += R * stepLen;
        stepLen *= 1.13;
        vec2 uv = project(pos);
        if (outside(uv) || pos.z > -0.05) break;
        float sd = depthAt(uv);
        vec3 S = viewAt(uv, sd);
        float behind = S.z - pos.z;
        if (behind > 0.0 && behind < 0.4 + stepLen && sd < 0.99999) {
          vec3 a = prev;
          vec3 b = pos;
          for (int j = 0; j < 5; j++) {
            vec3 m = (a + b) * 0.5;
            vec2 u = project(m);
            vec3 s = viewAt(u, depthAt(u));
            if (s.z - m.z > 0.0) b = m;
            else a = m;
          }
          hit = project(b);
          travel = float(i) / 28.0;
          break;
        }
      }
      if (hit.x >= 0.0) {
        vec3 c = texture2D(inputBuffer, hit).rgb;
        float l = dot(c, LUMA);
        vec2 e = smoothstep(vec2(0.0), vec2(0.1), hit) * (1.0 - smoothstep(vec2(0.9), vec2(1.0), hit));
        float fresnel = pow(1.0 - max(dot(-V, N), 0.0), 3.0);
        float w = mask * e.x * e.y * (1.0 - travel) * (0.3 + 0.7 * fresnel) * settings.x;
        result += c * (0.08 + smoothstep(0.7, 2.2, l)) * w;
      }
    }
  }
  if (settings.y > 0.0) {
    float radiusPx = min(settings.z * projection[1][1] * 0.5 * resolution.y / max(-P.z, 0.1), 140.0);
    vec3 acc = vec3(0.0);
    for (int i = 0; i < 12; i++) {
      float fi = (float(i) + noise) / 12.0;
      float angle = (float(i) + noise) * 2.39996;
      vec2 uv = vUv + vec2(cos(angle), sin(angle)) * sqrt(fi) * radiusPx / resolution;
      if (outside(uv)) continue;
      float sd = depthAt(uv);
      if (sd >= 0.99999) continue;
      vec3 S = viewAt(uv, sd);
      vec3 D = S - P;
      float dist = length(D);
      if (dist < 0.05 || dist > settings.z * 1.6) continue;
      vec3 dir = D / dist;
      float receive = max(dot(N, dir), 0.0);
      float emit = 0.35 + 0.65 * max(dot(normalAt(uv), -dir), 0.0);
      vec3 c = texture2D(inputBuffer, uv).rgb;
      acc += c * (0.05 + smoothstep(0.7, 2.2, dot(c, LUMA))) * receive * emit / (1.0 + dist * dist * 0.6);
    }
    result += acc * (settings.y / 12.0);
  }
  gl_FragColor = vec4(result, 1.0);
}
`;

export class IndirectLightPass extends DepthReadingPass {
  constructor(camera: Camera, normals: Texture) {
    super("LuzIndireta", camera, INDIRECT_FRAGMENT, {
      inputBuffer: new Uniform(null),
      normalBuffer: new Uniform(normals),
      projection: new Uniform(new Matrix4()),
      cameraWorld: new Uniform(new Matrix4()),
      resolution: new Uniform(new Vector2(1, 1)),
      settings: new Uniform(new Vector4(0, 0, 2.5, 0)),
    }, 0.5, true);
  }

  get texture(): Texture {
    return this.renderTarget.texture;
  }

  /** Forças do reflexo e da luz indireta (0 apaga cada um) e o raio da indireta (m). */
  set strengths(v: { ssr: number; gi: number; radius: number }) {
    (this.material.uniforms["settings"]!.value as Vector4).set(v.ssr, v.gi, v.radius, 0);
  }

  protected override resized(width: number, height: number): void {
    (this.material.uniforms["resolution"]!.value as Vector2).set(Math.max(1, width), Math.max(1, height));
  }

  protected override prepare(input: Texture): void {
    const u = this.material.uniforms;
    u["inputBuffer"]!.value = input;
    (u["projection"]!.value as Matrix4).copy(this.view.projectionMatrix);
    (u["cameraWorld"]!.value as Matrix4).copy(this.view.matrixWorld);
  }
}

/**
 * Junta o reflexo e a luz indireta (meia resolução) à imagem: cruz de 5 amostras (o borrão que tira o
 * ruído por pixel), somada.
 */
const INDIRECT_EFFECT = /* glsl */ `
uniform sampler2D indirectBuffer;
uniform vec2 indirectTexel;
void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  // 3 × 3 amostras bilineares a 1,5 texel (cobrem ~6 × 6 texels da meia resolução): tira o ruído por pixel.
  vec3 s = vec3(0.0);
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      float w = (i == 0 ? 2.0 : 1.0) * (j == 0 ? 2.0 : 1.0);
      s += texture2D(indirectBuffer, uv + vec2(float(i), float(j)) * indirectTexel * 1.5).rgb * w;
    }
  }
  outputColor = vec4(inputColor.rgb + s / 16.0, inputColor.a);
}
`;

export class IndirectEffect extends Effect {
  constructor(pass: IndirectLightPass) {
    super("IndirectEffect", INDIRECT_EFFECT, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, Uniform>([
        ["indirectBuffer", new Uniform(pass.texture)],
        ["indirectTexel", new Uniform(new Vector2(1, 1))],
      ]),
    });
  }

  override setSize(width: number, height: number): void {
    (this.uniforms.get("indirectTexel")!.value as Vector2).set(2 / Math.max(1, width), 2 / Math.max(1, height));
  }
}

/**
 * O sol na lente (G4.3 e G5.5): raios crepusculares (marcha de 24 amostras do pixel ao sol na máscara
 * do céu: as copas recortam os feixes) e o reflexo de lente (fantasmas na linha sol–centro, um halo e o
 * risco anamórfico). Tudo pesado pela visibilidade do sol (o céu em volta dele). `sol`: x = raios,
 * y = reflexo (0 com "Reduzir lampejos"), z = 1 com o sol na frente da câmera, w = livre.
 */
const SOL_EFFECT = /* glsl */ `
uniform vec2 sunUv;
uniform vec3 sunColor;
uniform vec4 sol;
float sky(const in vec2 uv) {
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return 0.0;
  return step(0.99999, readDepth(uv));
}
float ghost(const in vec2 uv, const in vec2 at, const in float radius) {
  float d = length((uv - at) * vec2(aspect, 1.0));
  return (1.0 - smoothstep(radius * 0.55, radius, d)) * 0.6 + exp(-abs(d - radius * 0.9) * 140.0) * 0.4;
}
void mainImage(const in vec4 inputColor, const in vec2 uv, const in float depth, out vec4 outputColor) {
  if (sol.z <= 0.0) {
    outputColor = inputColor;
    return;
  }
  // Visibilidade do sol: o céu num anel em volta dele.
  float vis = sky(sunUv) * 0.2;
  for (int i = 0; i < 8; i++) {
    float a = float(i) * 0.785398;
    vis += sky(sunUv + vec2(cos(a), sin(a)) * vec2(1.0 / aspect, 1.0) * 0.012) * 0.1;
  }
  vec2 toSun = sunUv - uv;
  float far = length(toSun * vec2(aspect, 1.0));
  vec3 add = vec3(0.0);
  if (sol.x > 0.0 && far < 0.9) {
    vec2 stepUv = toSun / 24.0;
    float noise = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
    vec2 p = uv + stepUv * noise;
    float rays = 0.0;
    float decay = 1.0;
    for (int i = 0; i < 24; i++) {
      rays += sky(p) * decay;
      decay *= 0.955;
      p += stepUv;
    }
    add += sunColor * (rays / 24.0) * pow(1.0 - far / 0.9, 2.0) * sol.x * (0.4 + 0.6 * vis);
  }
  if (sol.y > 0.0 && vis > 0.0) {
    vec2 axis = vec2(0.5) - sunUv;
    float g = ghost(uv, sunUv + axis * 0.55, 0.035) * 0.5;
    g += ghost(uv, sunUv + axis * 1.25, 0.06) * 0.35;
    g += ghost(uv, sunUv + axis * 1.6, 0.025) * 0.6;
    g += ghost(uv, sunUv + axis * 2.1, 0.11) * 0.18;
    float halo = exp(-abs(far - 0.32) * 40.0) * 0.12;
    float streak = exp(-abs(uv.y - sunUv.y) * 260.0) * exp(-abs(toSun.x * aspect) * 2.2) * 0.9;
    vec3 tint = mix(sunColor, sunColor * vec3(0.7, 0.95, 1.15), 0.5);
    add += (tint * (g + halo) + sunColor * streak) * sol.y * vis;
  }
  outputColor = vec4(inputColor.rgb + add, inputColor.a);
}
`;

export class SolEffect extends Effect {
  constructor() {
    super("SolEffect", SOL_EFFECT, {
      attributes: EffectAttribute.CONVOLUTION | EffectAttribute.DEPTH,
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, Uniform>([
        ["sunUv", new Uniform(new Vector2(0.5, 0.5))],
        ["sunColor", new Uniform(new Vector3(1, 1, 1))],
        ["sol", new Uniform(new Vector4())],
      ]),
    });
  }

  /** O sol na tela (uv), a cor (linear, com a força) e as forças dos raios e do reflexo; `front` 0 apaga. */
  set sun(s: { uv: Vector2; color: Vector3; rays: number; flare: number; front: boolean }) {
    (this.uniforms.get("sunUv")!.value as Vector2).copy(s.uv);
    (this.uniforms.get("sunColor")!.value as Vector3).copy(s.color);
    (this.uniforms.get("sol")!.value as Vector4).set(s.rays, s.flare, s.front ? 1 : 0, 0);
  }
}

/**
 * Aberração cromática (G5.6) no passe principal, sem passe próprio e sem o atributo de convolução (a do
 * pacote o declara, e só cabe um efeito assim por passe). Soma à cor já corrigida a DIFERENÇA de vermelho e azul da cena
 * deslocada na direção radial, só perto da borda (`offset` em uv; 0 apaga).
 */
const ABERRACAO_EFFECT = /* glsl */ `
uniform float offset;
vec3 hdrPeak(const in vec3 c) {
  return c / max(max(c.r, max(c.g, c.b)), 1.0);
}
void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  if (offset <= 0.0) {
    outputColor = inputColor;
    return;
  }
  vec2 d = uv - 0.5;
  float m = smoothstep(0.25, 0.75, length(d * vec2(aspect, 1.0)));
  vec2 o = d * (offset * 2.0 * m);
  // As amostras da cena em HDR (as estrelas passam de 5) comprimidas pelo maior canal, como a correção
  // de cor faz: a franja fica do tamanho do que a imagem final mostra.
  vec3 base = hdrPeak(texture2D(inputBuffer, uv).rgb);
  float r = hdrPeak(texture2D(inputBuffer, uv + o).rgb).r;
  float b = hdrPeak(texture2D(inputBuffer, uv - o).rgb).b;
  outputColor = vec4(max(inputColor.rgb + vec3(r - base.r, 0.0, b - base.b), 0.0), inputColor.a);
}
`;

export class AberracaoEffect extends Effect {
  constructor(offset: number) {
    super("AberracaoEffect", ABERRACAO_EFFECT, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, Uniform>([["offset", new Uniform(offset)]]),
    });
  }

  set offset(value: number) {
    (this.uniforms.get("offset") as Uniform<number>).value = value;
  }
}
